import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, Users, Shield, Truck, Eye, EyeOff, X,
  CheckCircle2, ArrowRight, Loader2, LogOut, Settings,
  Zap, Star, MessageCircle, Send, Paperclip,
  TrendingUp, FileCheck, AlertCircle, Lock,
  ChevronLeft, ChevronRight, Award, Flame, Trophy,
  Calendar, DollarSign, FileText, Trash2, ExternalLink,
  Search, ChevronDown, Upload, Phone, Share2, Camera, Smile, ArrowLeft,
  Check, CheckCircle, XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ACCENT = "#FF4D00";
const GOLD   = "#F5C842";
const DARK   = "#0A0A0A";
const CARD   = "#161618";
const MID    = "#1C1C1E";
const MARKET_TOKEN_KEY = "hylas_market_token";
const GREEN_SUCCESS = "#22c55e";

const HUB_META = {
  carrier:   { icon: Building2, label: "Carriers",   accent: "#FF4D00", glow: "255,77,0",    desc: "Bid on drivers",      badge: "HIRING",     badgeBg: "rgba(255,77,0,0.15)" },
  recruiter: { icon: Users,     label: "Recruiters", accent: "#F5C842", glow: "245,200,66",  desc: "Manage pipeline",     badge: "TALENT",     badgeBg: "rgba(245,200,66,0.15)" },
  safety:    { icon: Shield,    label: "Safety",     accent: "#38BDF8", glow: "56,189,248",  desc: "Vet & approve",       badge: "COMPLIANCE", badgeBg: "rgba(56,189,248,0.15)" },
  driver:    { icon: Truck,     label: "Drivers",    accent: "#4ADE80", glow: "74,222,128",  desc: "Your market value",   badge: "PROFILES",   badgeBg: "rgba(74,222,128,0.15)" },
  admin:     { icon: Star,      label: "Admin",      accent: "#A78BFA", glow: "167,139,250", desc: "Full access",         badge: "SYSTEM",     badgeBg: "rgba(167,139,250,0.15)" },
};

function timeAgo(isoStr) {
  if (!isoStr) return "";
  const diff = Date.now() - new Date(isoStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ══════════════════════════════════════════════════════════════════════════════
// ── CHAT ROOM ─────────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
const CHAT_EMOJIS = ["👍", "❤️", "🔥", "😂", "👎", "💯", "🤝"];
const CHAT_EMOJI_PICKER = [
  "😀","😂","😎","🤔","😤","😅","🙏","❤️","😍","🥲",
  "👍","👎","✌️","🤙","💪","👏","🤝","👀","🫡","🫶",
  "🚛","🚚","⛽","🔧","📦","🏆","💰","💵","✅","❌",
  "🔥","💯","🎉","⭐","💬","📝","⚡","🎯","🆙","💥",
];
const CHAT_FILE_ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/gif"];

function ChatRoom({ room, roomLabel, token, hub }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [openThread, setOpenThread] = useState(null);
  const [threadMsgs, setThreadMsgs] = useState([]);
  const [showEmoji, setShowEmoji] = useState(null);
  const [showInputEmoji, setShowInputEmoji] = useState(false);
  const [warning, setWarning] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const canUpload = ["recruiter", "safety", "admin"].includes(hub);

  const loadMessages = useCallback(async () => {
    try {
      const msgs = await api.marketGetChat(token, room);
      setMessages(msgs);
    } catch { /* ignore */ }
  }, [token, room]);

  useEffect(() => { loadMessages(); }, [loadMessages]);
  useEffect(() => {
    const id = setInterval(loadMessages, 3000);
    return () => clearInterval(id);
  }, [loadMessages]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  function parseMentions(t) {
    const found = [];
    const re = /@([\w ]+?)(?=\s|$|,)/g;
    let m;
    while ((m = re.exec(t)) !== null) found.push(m[1].trim());
    return found;
  }

  function mention(name) {
    setText((t) => `${t}@${name} `);
    inputRef.current?.focus();
  }

  async function send(e) {
    e?.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    setWarning(null);
    try {
      const res = await api.marketSendMessage(token, {
        chat_room: room, message: text.trim(),
        parent_id: replyTo?.id || null, mentions: parseMentions(text),
      });
      if (res?.moderated) {
        if (res.banned) { localStorage.removeItem(MARKET_TOKEN_KEY); window.location.reload(); }
        else setWarning("Your message was removed: it contained contact info or promotional content. Next violation = permanent ban.");
      } else { setText(""); setReplyTo(null); await loadMessages(); if (openThread) loadThread(openThread); }
    } catch { toast.error("Failed to send."); }
    finally { setSending(false); }
  }

  async function loadThread(msgId) {
    try {
      const replies = await api.marketGetThread(token, room, msgId);
      setThreadMsgs(replies);
    } catch { setThreadMsgs([]); }
  }

  async function react(msgId, emoji) {
    try { await api.marketReactMessage(token, msgId, emoji); await loadMessages(); } catch { /* ignore */ }
    setShowEmoji(null);
  }

  function insertEmoji(em) {
    const el = inputRef.current;
    if (!el) { setText((t) => t + em); setShowInputEmoji(false); return; }
    const start = el.selectionStart ?? text.length;
    const end   = el.selectionEnd   ?? text.length;
    setText(text.slice(0, start) + em + text.slice(end));
    setShowInputEmoji(false);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + em.length, start + em.length); }, 0);
  }

  async function openThreadPanel(msgId) {
    if (openThread === msgId) { setOpenThread(null); setThreadMsgs([]); return; }
    setOpenThread(msgId);
    await loadThread(msgId);
  }

  async function handleFileUpload(file) {
    if (!canUpload) return;
    if (!CHAT_FILE_ALLOWED_TYPES.includes(file.type) && !file.name.match(/\.(pdf|jpg|jpeg|png|webp|gif)$/i)) {
      toast.error("Only PDF and images (JPG, PNG, WEBP) are allowed.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large — max 10 MB."); return; }
    setFileUploading(true);
    setUploadPct(0);
    try {
      const res = await api.marketChatUploadFile(token, file, (e) => {
        if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
      });
      await api.marketSendMessage(token, {
        chat_room: room, message: `📎 ${res.filename}`,
        file_path: res.path, parent_id: replyTo?.id || null, mentions: [],
      });
      setReplyTo(null);
      await loadMessages();
      toast.success("File shared.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setFileUploading(false); setUploadPct(0); }
  }

  function onDrop(e) {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  }

  function renderMsg(txt) {
    const parts = txt.split(/(@[\w ]+)/g);
    return parts.map((p, i) =>
      p.startsWith("@") ? <span key={i} style={{ color: GOLD }} className="font-semibold">{p}</span> : p
    );
  }

  const HUB_COLORS = { carrier: ACCENT, recruiter: GOLD, safety: GOLD, driver: ACCENT, admin: "#FF6B35" };
  const senderNames = [...new Set(messages.map((m) => m.sender_name).filter(Boolean))];
  const atMatches = text.match(/@(\w*)$/) ? senderNames.filter((n) => n.toLowerCase().includes((text.match(/@(\w*)$/)?.[1] || "").toLowerCase())) : [];

  return (
    <div
      data-testid="market-chat-section"
      style={{ background: MID, border: "1px solid rgba(255,255,255,0.07)" }}
      onDragOver={canUpload ? (e) => { e.preventDefault(); setDragOver(true); } : undefined}
      onDragLeave={canUpload ? () => setDragOver(false) : undefined}
      onDrop={canUpload ? onDrop : undefined}
    >
      <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
        <MessageCircle className="w-4 h-4" style={{ color: ACCENT }} />
        <span className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">{roomLabel}</span>
        {canUpload && <span className="ml-2 text-[9px] text-white/25 uppercase">· drag & drop files to share</span>}
        <span className="ml-auto w-2 h-2 rounded-full animate-pulse" style={{ background: ACCENT }} />
      </div>

      {/* Drag-over overlay */}
      {dragOver && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none" style={{ background: "rgba(255,77,0,0.08)", border: "2px dashed rgba(255,77,0,0.5)" }}>
          <p className="font-head text-sm uppercase tracking-wider" style={{ color: ACCENT }}>Drop to share</p>
        </div>
      )}

      {warning && (
        <div className="mx-4 mt-3 px-3 py-2 text-xs text-yellow-300 bg-yellow-400/10 border border-yellow-400/30 flex items-start gap-2">
          <span className="mt-0.5 shrink-0">⚠</span>
          <span>{warning}</span>
          <button className="ml-auto shrink-0 text-yellow-300/60 hover:text-yellow-300" onClick={() => setWarning(null)}>✕</button>
        </div>
      )}

      {replyTo && (
        <div className="mx-4 mt-2 px-3 py-1.5 flex items-center gap-2 text-xs" style={{ background: "rgba(255,77,0,0.1)", borderLeft: `2px solid ${ACCENT}` }}>
          <span className="text-white/40">Replying to</span>
          <span style={{ color: ACCENT }} className="font-semibold">{replyTo.sender_name}</span>
          <span className="text-white/40 truncate flex-1">· {replyTo.message.slice(0, 40)}</span>
          <button className="text-white/30 hover:text-white" onClick={() => setReplyTo(null)}>✕</button>
        </div>
      )}

      <div ref={scrollRef} className="overflow-y-auto px-4 py-3 space-y-4" style={{ height: "300px" }}>
        {messages.length === 0 && <p className="text-center text-white/20 text-xs py-8">No messages yet. Start the conversation.</p>}
        {messages.map((m) => (
          <div key={m.id} className="group space-y-1">
            {/* System message — status update notification */}
            {m.is_system ? (
              <div className="flex items-center gap-2 py-1 px-3"
                style={{ background: "rgba(56,189,248,0.04)", border: "1px solid rgba(56,189,248,0.12)" }}>
                <Shield className="w-3 h-3 shrink-0" style={{ color: "#38BDF8" }} />
                <p className="font-head text-[9px] uppercase tracking-[0.1em] text-white/50 flex-1">{m.message}</p>
                <span className="text-[9px] text-white/20 shrink-0">{timeAgo(m.created_at)}</span>
              </div>
            ) : (
            <>
            <div className="flex items-center gap-2">
              <span className="font-head text-[9px] font-bold uppercase" style={{ color: HUB_COLORS[m.sender_hub] || "#888" }}>{m.sender_name}</span>
              <span className="text-[9px] text-white/25 uppercase">{m.sender_hub}</span>
              <span className="text-[9px] text-white/20 ml-auto">{timeAgo(m.created_at)}</span>
            </div>

            {/* File attachment display */}
            {m.file_path && m.message.startsWith("📎 ") ? (
              <div>
                {m.file_path.match(/\.(jpg|jpeg|png|webp|gif)$/i) ? (
                  <a href={`${apiBase}/api/market/admin/file/${encodeURIComponent(m.file_path)}`} target="_blank" rel="noopener noreferrer">
                    <img
                      src={`${apiBase}/api/market/admin/file/${encodeURIComponent(m.file_path)}`}
                      alt="chat attachment"
                      className="max-h-40 max-w-xs object-contain mt-1"
                      style={{ border: "1px solid rgba(255,255,255,0.1)" }}
                    />
                  </a>
                ) : (
                  <a
                    href={`${apiBase}/api/market/admin/file/${encodeURIComponent(m.file_path)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs transition-colors"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: ACCENT }}
                  >
                    📎 {m.message.replace("📎 ", "")}
                  </a>
                )}
              </div>
            ) : (
              <p className="text-sm text-white/80 leading-relaxed">{renderMsg(m.message)}</p>
            )}

            {m.reactions && Object.keys(m.reactions).filter((e) => m.reactions[e]?.length > 0).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {Object.entries(m.reactions).map(([emoji, users]) =>
                  users.length > 0 ? (
                    <button key={emoji} onClick={() => react(m.id, emoji)}
                      className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs"
                      style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)" }}>
                      {emoji} <span className="text-white/60">{users.length}</span>
                    </button>
                  ) : null
                )}
              </div>
            )}

            <div className="flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
              <button className="text-[9px] text-white/30 hover:text-white/70 uppercase tracking-wider" onClick={() => { setReplyTo(m); inputRef.current?.focus(); }}>↩ Reply</button>
              {m.reply_count > 0 && (
                <button className="text-[9px] uppercase tracking-wider" style={{ color: ACCENT }} onClick={() => openThreadPanel(m.id)}>
                  {openThread === m.id ? "Hide" : `${m.reply_count} repl${m.reply_count === 1 ? "y" : "ies"}`}
                </button>
              )}
              <div className="relative">
                <button className="text-[9px] text-white/30 hover:text-white/70" onClick={() => setShowEmoji(showEmoji === m.id ? null : m.id)}>React</button>
                {showEmoji === m.id && (
                  <div className="absolute bottom-5 left-0 z-20 flex gap-1 p-1.5" style={{ background: CARD, border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6 }}>
                    {CHAT_EMOJIS.map((em) => <button key={em} className="text-base hover:scale-125 transition-transform" onClick={() => react(m.id, em)}>{em}</button>)}
                  </div>
                )}
              </div>
              <button className="text-[9px] text-white/30 hover:text-white/70" onClick={() => mention(m.sender_name)}>@ Mention</button>
            </div>

            {openThread === m.id && (
              <div className="ml-4 mt-1 space-y-2 pl-3" style={{ borderLeft: "2px solid rgba(255,255,255,0.08)" }}>
                {threadMsgs.length === 0 && <p className="text-[10px] text-white/30">No replies yet.</p>}
                {threadMsgs.map((r) => (
                  <div key={r.id}>
                    <div className="flex items-center gap-2">
                      <span className="font-head text-[9px] font-bold uppercase" style={{ color: HUB_COLORS[r.sender_hub] || "#888" }}>{r.sender_name}</span>
                      <span className="text-[9px] text-white/20 ml-auto">{timeAgo(r.created_at)}</span>
                    </div>
                    <p className="text-xs text-white/70">{renderMsg(r.message)}</p>
                  </div>
                ))}
              </div>
            )}
            </>
            )}
          </div>
        ))}
      </div>

      {atMatches.length > 0 && (
        <div className="mx-4 mb-1 flex flex-wrap gap-1">
          {atMatches.map((n) => (
            <button key={n} onClick={() => mention(n)} className="text-[9px] px-2 py-0.5 uppercase tracking-wider"
              style={{ background: "rgba(255,77,0,0.15)", color: ACCENT, border: `1px solid ${ACCENT}40` }}>
              @{n}
            </button>
          ))}
        </div>
      )}

      {fileUploading && (
        <div className="mx-4 mb-2">
          <div className="w-full h-1" style={{ background: "rgba(255,255,255,0.08)" }}>
            <div className="h-1 transition-all" style={{ width: `${uploadPct}%`, background: ACCENT }} />
          </div>
          <p className="text-[9px] text-white/40 mt-0.5">Uploading… {uploadPct}%</p>
        </div>
      )}

      <form onSubmit={send} className="px-4 py-3 flex items-center gap-2 relative" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
        {canUpload && (
          <>
            <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.gif" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); e.target.value = ""; }} />
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={fileUploading}
              className="p-2 text-white/30 hover:text-white/70 transition-colors disabled:opacity-40"
              title="Attach file (PDF or image, max 10 MB)">
              <Paperclip className="w-4 h-4" />
            </button>
          </>
        )}

        {/* Emoji picker */}
        <div className="relative">
          <button
            type="button"
            data-testid="chat-emoji-picker-btn"
            onClick={() => setShowInputEmoji((v) => !v)}
            className="p-2 text-white/30 hover:text-yellow-400 transition-colors"
            title="Insert emoji"
          >
            <Smile className="w-4 h-4" />
          </button>
          {showInputEmoji && (
            <div
              className="absolute bottom-10 left-0 z-30 p-2 grid gap-0.5"
              style={{
                background: "#1a1a1a",
                border: "1px solid rgba(255,255,255,0.12)",
                gridTemplateColumns: "repeat(8, 1fr)",
                width: "220px",
              }}
            >
              {CHAT_EMOJI_PICKER.map((em) => (
                <button
                  key={em}
                  type="button"
                  onClick={() => insertEmoji(em)}
                  className="text-lg p-1 hover:bg-white/10 transition-colors leading-none"
                  style={{ lineHeight: 1 }}
                >
                  {em}
                </button>
              ))}
            </div>
          )}
        </div>

        <input
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type a message… (@ to mention)"
          className="flex-1 bg-[#111] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#FF4D00] transition-colors"
        />
        <button type="submit" disabled={sending || !text.trim()} className="p-2 transition-colors disabled:opacity-40" style={{ background: ACCENT, color: "white" }}>
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// ── SETUP WIZARD ─────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

export { ChatRoom };
